import { environment } from '@environment';
import { SitePlatform } from 'core/enums/site-platform';
import crypto from 'crypto';

export class Util {
  public static isEmpty(value: string | null | undefined) {
    return value === null || value === undefined || `${value}`?.trim() === '';
  }

  public static BaseUrl =
    window.location.origin ||
    `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;

  static GetConfigFromURL(): {
    platform: SitePlatform,
    locale: string,
    language: string,
    countryCode: string,
  } {
    const path = window.location.pathname;
    const gotvMatch = path.match(/\/([a-z]{2})-([a-z]{2})\/mygotv/);
    const dstvMatch = path.match(/\/([a-z]{2})-([a-z]{2})\/mydstv/);

    if (dstvMatch && dstvMatch.length > 1) {
      const language = dstvMatch[1].replace('/', '');
      const countryCode = dstvMatch[2].replace('/', '');

      return {
        platform: SitePlatform.dstv,
        locale: `${language}-${countryCode}`,
        language,
        countryCode
      }
    }

    if (gotvMatch && gotvMatch.length > 1) {
      const language = gotvMatch[1].replace('/', '');
      const countryCode = gotvMatch[2].replace('/', '');

      return {
        platform: SitePlatform.gotv,
        locale: `${language}-${countryCode}`,
        language,
        countryCode
      }
    }

    const [language, countryCode] = environment.DEFAULT_LOCALE.split('-');
    return {
      platform: SitePlatform.dstv,
      locale: environment.DEFAULT_LOCALE,
      language,
      countryCode
    };
  }

  static LangFromUrl() {
    return Util.LocaleFromUrl().split("-")[0];
  }

  static PlatformFromUrl() {
    return Util.GetConfigFromURL().platform;
  }

  public static LocaleFromUrl = (): string => {
    return Util.GetConfigFromURL().locale;
  };

  public static SetUrlCulture(url: string) {
    url = url.replace('{culture}', Util.LocaleFromUrl);
    return url;
  }

  public static FindAndReplace = (text: string, oldText: string, newText: string) => {
    return text.replace(oldText, newText);
  }

  public static BaseLocation = `${Util.BaseUrl}/${Util.LocaleFromUrl()}`;

  public static CurrentLocation = (includeSearch = true) => {
    const hash = window.location.hash;
    // const searchParts = window.location.href.split('?');
    const search = window.location.search; // searchParts.length > 1 ? searchParts[1] : ''
    const route = `${window.location.origin}${window.location.pathname}`;
    return `${route}${hash}${includeSearch ? search : ''}`;
  };

  public static GetRedirectUrl = (appPath: string = '') => {
    let url = `${Util.BaseUrl}${window.location.pathname}${appPath}`;
    return url;
  };

  public static AddQueryParams = (path: string, key: string, value: string) => {
    return path.includes('?') ? `${path}&${key}=${value}` : `${path}?${key}=${value}`;
  };

  public static RemoveAllQueryParams = () => {
    const search = window.location.search.toString();
    if (search.length < 2) {
      return;
    }
    window.history.replaceState(null, '', window.location.href.replace(search, ''));
  };

  public static RemoveConnectParams = () => {
    if (!this.CheckUrlForConnectHash(window.location.href)) {
      return;
    }

    let url = window.location.href;
    const hash = window.location.hash;

    if (hash) {
      url = url.replace(hash, '');
    }

    window.history.replaceState(null, '', url);
  };

  public static CheckUrlForConnectHash = (url: string) => {
    const hasAccessToken = url.includes('access_token') || url.includes('auth_code');
    return hasAccessToken;
  };

  public static ParseConnectUrlHash = () => {
    const hash = window.location.hash;
    const parts = hash.split("#");
    const search = parts[parts.length - 1];

    const urlParams = new URLSearchParams(search);

    const o = {} as any;

    for (const [key, value] of urlParams.entries()) {
      o[key] = value;
    }

    if (o.expires_in) {
      const expiresIn = parseInt(o.expires_in);
      const expires = new Date();
      const secs = expires.getSeconds() + expiresIn;
      expires.setSeconds(secs);
      o.expires_time = expires.toISOString();
    }

    return o;
  };

  public static ArrayUniqBy = <T>(array: T[], key: (value: T) => string) => {
    const seen = new Set();
    return array.filter((item) => {
      const k = key(item);
      return seen.has(k) ? false : seen.add(k);
    });
  };

  public static ArrayInsert = <T>(arr: any[], index: number, ...newItems: T[]): T[] => [
    // part of the array before the specified index
    ...arr.slice(0, index),
    // inserted items
    ...newItems,
    // part of the array after the specified index
    ...arr.slice(index),
  ];

  public static GetExponentialBackoff(count: number, maxDelay: number): number {
    return Math.min(Math.pow(2, count) * 1000, maxDelay);
  }

  public static ObjectToMap = <K, T>(
    object: { [key: number]: number | string } | { [key: string]: number | string }
  ): Map<K, T> => {
    const map = new Map();
    Object.keys(object).forEach((key) => map.set(key, object[key as keyof object]));
    return map;
  };

  public static GetImageAsset = (image: string) => {
    return `${environment.SOURCE_FOLDER}assets/img/${image}`;
  };

  public static GetMediaPath = (image: string) => {
    if (environment.production) {
      return image;
    }

    image = `${environment.UMBRACO_BASE_URI}${image}`;
    return image.startsWith('//') ? image.substring(1) : image;
  };

  public static GetScreenWidth = (): number => {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  };

  public static PercentToHex = (p: number) => {
    return Math.round((Math.max(0, Math.min(100, p)) / 100) * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  };

  public static OpenBlob = (fileName: string, blob: Blob, type = 'application/pdf') => {
    const a = window.document.createElement('a');

    a.href = window.URL.createObjectURL(new Blob([blob], { type }));
    a.download = `${fileName}`;
    a.target = `_blank`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  public static Base64ToBlob(b64Data: string, contentType = 'application/pdf', sliceSize = 512) {
    const byteCharacters = window.atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  // NOTE: Safari/Opera/Firefox on iOS do not download the file with the below method
  public static DownloadBase64File = (fileName: string, pdfString: string) => {
    const a = window.document.createElement('a');

    a.href = `data:application/pdf;base64,${pdfString}`;
    a.download = `${fileName}`;
    a.target = `_blank`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  public static ToLowerCaseFirst = (str: string) => {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  public static ConvertKeysToLowerCaseFirst<T extends object>(obj: T): any {
    if (Array.isArray(obj)) {
      const list: any[] = [];
      obj.forEach(x => list.push(this.ConvertKeysToLowerCaseFirst(x)));
      return list;
    }

    const newObj: any = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = this.ToLowerCaseFirst(key);
        const value = obj[key];

        newObj[newKey] = (value && typeof value === 'object' || Array.isArray(value))
          ? this.ConvertKeysToLowerCaseFirst(value)
          : value;
      }
    }

    return newObj;
  }

  public static convertToHash = (value: string) => {
    if (Util.isEmpty(value)) {
      return '';
    } else {
      return crypto.createHash('sha256').update(value).digest('hex');
    }
  };

  public static Capitalize = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  public static ScrollToTop() {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  public static GetCssClassName(classes: string[]) {
    if (!classes) {
      return "";
    }

    classes = classes.filter(x => x?.length && x !== 'undefined');
    return classes.join(" ").trim();
  }

  public static TryParseJSON = (data?: string) => {
    if (!data) {
      return '';
    }
    try {
      return JSON.parse(data);
    } catch (e) {
      return data;
    }
  };
}